import { lazy, Suspense, useEffect } from "react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { NelyraLogo } from "@/components/NelyraLogo";
import { HeroSection } from "@/components/landing/HeroSection";
import { EnterpriseDemoSection } from "@/components/landing/EnterpriseDemoSection";

const LandingBelowFold = lazy(() => import("@/components/landing/LandingBelowFold"));
const prefetchAuthChunk = () => import("@/components/AuthenticatedApp");

export default function LandingPage() {
  useEffect(() => {
    const id = typeof requestIdleCallback === "function"
      ? requestIdleCallback(prefetchAuthChunk)
      : setTimeout(prefetchAuthChunk, 3000) as unknown as number;
    return () => {
      typeof cancelIdleCallback === "function"
        ? cancelIdleCallback(id)
        : clearTimeout(id);
    };
  }, []);

  return (
    <div className="min-h-screen bg-background dark">
      <a
        href="#main-content"
        className="sr-only focus:not-sr-only focus:absolute focus:top-2 focus:left-2 focus:z-[100] focus:px-4 focus:py-2 focus:bg-primary focus:text-primary-foreground focus:rounded-md focus:text-sm"
      >
        Zum Inhalt springen
      </a>

      {/* Header */}
      <header className="sticky top-0 z-50 glass-header" role="banner" style={{ paddingTop: "max(env(safe-area-inset-top), 0px)" }}>
        <div className="container flex h-16 items-center justify-between px-4">
          <div className="flex items-center gap-3 min-w-0">
            <NelyraLogo size="sm" className="shrink-0" />
            <span className="text-xl font-semibold tracking-tight text-foreground truncate">
              Nelyra
            </span>
          </div>
          <nav className="flex items-center gap-1 sm:gap-2 shrink-0" aria-label="Hauptnavigation">
            <Link to="/login">
              <Button variant="ghost" size="sm" className="text-xs sm:text-sm px-2 sm:px-3">
                Anmelden
              </Button>
            </Link>
            <Link to="/register">
              <Button size="sm" className="text-xs sm:text-sm px-2 sm:px-3">
                Registrieren
              </Button>
            </Link>
          </nav>
        </div>
      </header>

      <main id="main-content">
        <HeroSection />
        <div className="text-center pb-2 pt-8">
          <span className="text-xs font-semibold uppercase tracking-widest text-muted-foreground">
            Testen Sie Nelyra jetzt — ohne Registrierung
          </span>
        </div>
        <EnterpriseDemoSection />
        <Suspense fallback={null}>
          <LandingBelowFold />
        </Suspense>
      </main>

      {/* Footer */}
      <footer className="border-t border-border py-10 pb-[calc(2.5rem+env(safe-area-inset-bottom,0px))]" role="contentinfo">
        <div className="container px-4">
          <div className="mx-auto max-w-4xl">
            <div className="grid gap-8 sm:grid-cols-3 mb-8">
              <div>
                <div className="flex items-center gap-2 mb-3">
                  <NelyraLogo size="sm" />
                  <span className="font-semibold text-foreground">Nelyra</span>
                </div>
                <p className="text-sm text-muted-foreground leading-relaxed">
                  KI-gestützte Textkorrektur für professionelle deutsche Kommunikation.
                </p>
              </div>
              <div>
                <h4 className="font-semibold text-foreground text-sm mb-3">Produkt</h4>
                <nav className="flex flex-col gap-2 text-sm" aria-label="Produkt-Links">
                  <Link to="/pricing" className="text-muted-foreground hover:text-foreground transition-colors duration-200">Preise</Link>
                  <Link to="/hilfe" className="text-muted-foreground hover:text-foreground transition-colors duration-200">Hilfe</Link>
                  <Link to="/status" className="text-muted-foreground hover:text-foreground transition-colors duration-200">Status</Link>
                  <Link to="/bewertungen" className="text-muted-foreground hover:text-foreground transition-colors duration-200">Bewertungen</Link>
                </nav>
              </div>
              <div>
                <h4 className="font-semibold text-foreground text-sm mb-3">Rechtliches</h4>
                <nav className="flex flex-col gap-2 text-sm" aria-label="Rechtliche Links">
                  <Link to="/impressum" className="text-muted-foreground hover:text-foreground transition-colors duration-200">Impressum</Link>
                  <Link to="/datenschutz" className="text-muted-foreground hover:text-foreground transition-colors duration-200">Datenschutz</Link>
                  <Link to="/agb" className="text-muted-foreground hover:text-foreground transition-colors duration-200">AGB</Link>
                  <Link to="/widerruf" className="text-muted-foreground hover:text-foreground transition-colors duration-200">Widerruf</Link>
                  <Link to="/nutzungsrichtlinien" className="text-muted-foreground hover:text-foreground transition-colors duration-200">Nutzungsrichtlinien</Link>
                  <Link to="/ki-transparenz" className="text-muted-foreground hover:text-foreground transition-colors duration-200">KI-Transparenz</Link>
                  <Link to="/sicherheit" className="text-muted-foreground hover:text-foreground transition-colors duration-200">Sicherheit</Link>
                  <Link to="/kontakt" className="text-muted-foreground hover:text-foreground transition-colors duration-200">Kontakt</Link>
                </nav>
              </div>
            </div>
            <div className="border-t border-border pt-6 text-center">
              <p className="text-xs text-muted-foreground">
                © {new Date().getFullYear()} Nelyra. Alle Rechte vorbehalten.
              </p>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
}
